/**
 * <discount-apply> custom element
 *
 * Wires the cart's "Enter promo code" form to Shopify's /discount/<CODE> endpoint
 * (sets the discount_code cookie that auto-applies at checkout) and validates
 * eligibility against /cart.js.
 *
 * Markup expected (rendered by snippets/cart-drawer.liquid and sections/main-cart-items.liquid):
 *
 *   <discount-apply class="cart-form__discount-wraper">
 *     <div class="cart-form__discount">
 *       <input id="discount" type="text" placeholder=" Enter promo code">
 *       <button class="apply" is="custom-button">…Apply…</button>
 *     </div>
 *     <ul class="discount-apply__badges" role="list">…rendered by Liquid…</ul>
 *     <div class="discount-apply__error" role="alert" aria-live="polite"></div>
 *   </discount-apply>
 */

class DiscountApply extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input');
    this.button = this.querySelector('button.apply');
    this.errorEl = this.querySelector('.discount-apply__error');
    this.badgesEl = this.querySelector('.discount-apply__badges');

    if (!this.input || !this.button) return;

    // Submit on Apply click
    this.button.addEventListener('click', this.onApply.bind(this));

    // Submit on Enter key
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.onApply(event);
      }
    });

    // Wire up "remove" buttons on existing badges
    this.bindBadgeRemoveHandlers();
  }

  bindBadgeRemoveHandlers() {
    if (!this.badgesEl) return;
    this.badgesEl.querySelectorAll('button[data-code]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.removeCode(btn.dataset.code);
      });
    });
  }

  async onApply(event) {
    if (event) event.preventDefault();

    const code = (this.input.value || '').trim();
    this.clearError();

    if (!code) {
      this.showError('Please enter a discount code');
      this.input.focus();
      return;
    }

    this.setLoading(true);

    try {
      // Step 1: hit /discount/<code> in a hidden iframe so Shopify writes the discount_code cookie
      // without forcing a full page navigation.
      await this.applyDiscountCookie(code);

      // Step 2: re-read the cart so we can confirm the code stuck and inspect applicability.
      const cart = await this.fetchCart();
      const applied = (cart.discount_codes || []).find(
        (d) => (d.code || '').toUpperCase() === code.toUpperCase()
      );

      if (!applied) {
        // Not even returned -> code is invalid or expired
        this.clearDiscountCookie();
        this.showError('Discount code is invalid or no longer available.');
        return;
      }

      if (!applied.applicable) {
        // Returned but not eligible (cart contents / customer / minimum / combination rule)
        this.clearDiscountCookie();
        this.showError('This code is not eligible for the items in your cart.');
        return;
      }

      // Step 3: success — clear input, refresh ALL cart UI areas
      this.input.value = '';

      // The theme's built-in partial refresh has a selector mismatch
      // (it looks for `.cart-drawer__footer` but the upper footer is `.drawer__footer`),
      // so we always do a full section swap to make sure badge + Item(s) Discount line
      // + Estimated total all stay in sync.
      await this.refreshCartSections();

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'discount-apply',
          cartData: cart,
        });
      }
    } catch (err) {
      console.error('[discount-apply] apply failed:', err);
      this.showError('Could not apply the code right now. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  async removeCode(code) {
    if (!code) return;
    this.clearError();
    this.setLoading(true);

    try {
      // Shopify's /discount/CODE endpoint replaces the cookie with the new code, so simply
      // clearing the cookie is the only reliable way to remove an applied code at the cart level.
      this.clearDiscountCookie();

      // Re-read /cart.js so totals & discount_codes refresh.
      await this.fetchCart();

      // Force-refresh both upper and lower footers (see refreshCartSections for why).
      await this.refreshCartSections();

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'discount-apply' });
      }
    } catch (err) {
      console.error('[discount-apply] remove failed:', err);
      this.showError('Could not remove the code. Please refresh and try again.');
    } finally {
      this.setLoading(false);
    }
  }

  applyDiscountCookie(code) {
    return new Promise((resolve, reject) => {
      let settled = false;
      const safetyTimeout = setTimeout(() => {
        if (settled) return;
        settled = true;
        try {
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        } catch (_) {}
        // Don't reject — the cookie may still be set even if onload didn't fire (it's a 302).
        resolve();
      }, 4000);

      const iframe = document.createElement('iframe');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.setAttribute('tabindex', '-1');
      iframe.style.position = 'absolute';
      iframe.style.width = '1px';
      iframe.style.height = '1px';
      iframe.style.opacity = '0';
      iframe.style.pointerEvents = 'none';
      iframe.style.left = '-9999px';

      iframe.addEventListener('load', () => {
        if (settled) return;
        settled = true;
        clearTimeout(safetyTimeout);
        // Give the browser a tick to finish writing the cookie before we read /cart.js.
        setTimeout(() => {
          try {
            if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
          } catch (_) {}
          resolve();
        }, 200);
      });

      iframe.addEventListener('error', (e) => {
        if (settled) return;
        settled = true;
        clearTimeout(safetyTimeout);
        try {
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        } catch (_) {}
        reject(e);
      });

      iframe.src = '/discount/' + encodeURIComponent(code);
      document.body.appendChild(iframe);
    });
  }

  clearDiscountCookie() {
    // Expire the discount_code cookie on every relevant path/domain.
    const expire = '; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    document.cookie = 'discount_code=' + expire;
    // Some Shopify shops scope the cookie to the apex domain; expire that variant too.
    const host = window.location.hostname;
    const apex = host.split('.').slice(-2).join('.');
    if (apex && apex !== host) {
      document.cookie = 'discount_code=' + expire + '; domain=.' + apex;
    }
  }

  async fetchCart() {
    const res = await fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!res.ok) throw new Error('cart.js fetch failed: ' + res.status);
    return res.json();
  }

  /**
   * Re-fetch the cart section and swap the parts that actually changed.
   *
   * Why this is needed: the theme's built-in cart.js refresh only swaps
   * `cart-drawer-items` and `.cart-drawer__footer`. But this theme's upper
   * totals area (with the discount input, Item(s) Discount line, etc.) sits
   * inside `.drawer__footer` — a different element — so it doesn't get
   * refreshed by the built-in path. We swap all three areas explicitly.
   */
  async refreshCartSections() {
    try {
      const isDrawer = !!document.querySelector('cart-drawer-items');
      const sectionId = isDrawer ? 'cart-drawer' : 'main-cart-items';

      const res = await fetch('/?sections=' + sectionId, {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!res.ok) throw new Error('section fetch failed: ' + res.status);
      const data = await res.json();
      if (!data || !data[sectionId]) throw new Error('section not in response');

      const parsed = new DOMParser().parseFromString(data[sectionId], 'text/html');

      // Selectors of the elements we want to swap. Order matters: items first,
      // then upper footer (discount-apply + Item(s) Discount), then lower footer
      // (Estimated total + Checkout). Anything missing on either side is skipped.
      const selectors = isDrawer
        ? ['cart-drawer-items', '.drawer__footer', '.cart-drawer__footer']
        : ['cart-items', '.cart__totals', '.cart-page-totals'];

      for (const selector of selectors) {
        const live = document.querySelector(selector);
        const fresh = parsed.querySelector(selector);
        if (live && fresh) live.replaceWith(fresh);
      }

      // After swap, our element reference is stale (the input/button live in
      // the new DOM). Re-bind so subsequent applies still work.
      const refreshedRoot = document.querySelector('discount-apply');
      if (refreshedRoot && refreshedRoot !== this) {
        // The browser will create a new instance via the custom element constructor.
        return;
      }

      // Same node was kept (rare): re-query our internal references.
      this.input = this.querySelector('input');
      this.button = this.querySelector('button.apply');
      this.errorEl = this.querySelector('.discount-apply__error');
      this.badgesEl = this.querySelector('.discount-apply__badges');
      this.bindBadgeRemoveHandlers();
    } catch (e) {
      console.error('[discount-apply] section refresh failed:', e);
      // Last resort: full page reload so the user sees the updated cart.
      window.location.reload();
    }
  }

  setLoading(state) {
    this.classList.toggle('is-loading', !!state);
    if (this.button) this.button.disabled = !!state;
    if (this.input) this.input.disabled = !!state;
  }

  showError(message) {
    if (!this.errorEl) {
      this.errorEl = document.createElement('div');
      this.errorEl.className = 'discount-apply__error';
      this.errorEl.setAttribute('role', 'alert');
      this.errorEl.setAttribute('aria-live', 'polite');
      this.appendChild(this.errorEl);
    }
    this.errorEl.textContent = message;
    this.errorEl.style.display = 'block';
  }

  clearError() {
    if (!this.errorEl) return;
    this.errorEl.textContent = '';
    this.errorEl.style.display = 'none';
  }
}

if (!customElements.get('discount-apply')) {
  customElements.define('discount-apply', DiscountApply);
}
